"use client"

import { useEffect, useState, type CSSProperties } from "react"
import { logoLetters, logoViewBox } from "./brand-logo-paths"
import { INTRO_MAX_MS, INTRO_SESSION_KEY } from "@/lib/brand-intro"
import { HeroBackground } from "./hero-background"

type LogoLetter = (typeof logoLetters)[number]["letter"]
const STAGES: { letters: LogoLetter[]; centerOffset: number; nodeIds: string[] }[] = [
  { letters: ["O"], centerOffset: 1600.599609, nodeIds: ["60:4"] },
  { letters: ["R"], centerOffset: 970.345703, nodeIds: ["60:5"] },
  { letters: ["K", "E", "S", "T"], centerOffset: -653.470703, nodeIds: ["60:6", "60:12", "60:11", "60:8"] },
]
// Figma 60:2 choreography, played once; keep the final logo while loading.
const TIMELINE_MS = 3150
const EXIT_MS = 300
const HOLD_END_MS = TIMELINE_MS - EXIT_MS
/** First-visit cover, bounded to four seconds including its exit fade. */
export function BrandIntro() {
  const [visible, setVisible] = useState(true)
  const [exiting, setExiting] = useState(false)
  const [exitDuration, setExitDuration] = useState(EXIT_MS)

  useEffect(() => {
    if (document.documentElement.dataset.intro === "seen") {
      setVisible(false)
      return
    }
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)")
    const headStart = Number(document.documentElement.dataset.introStart)
    const startedAt = Number.isFinite(headStart) && headStart > 0 ? headStart : Date.now()
    const remaining = () => Math.max(0, INTRO_MAX_MS - (Date.now() - startedAt))
    let cancelled = false
    let deadlineTimer: number | undefined
    let animationTimer: number | undefined
    let exitTimer: number | undefined
    const removeListeners: (() => void)[] = []
    const logo = document.querySelector(".brand-intro-logo")
    let pageReady = false
    let animationReady = preference.matches
    const finish = (force = false) => {
      if (cancelled || (!force && (!pageReady || !animationReady))) return
      cancelled = true
      window.clearTimeout(deadlineTimer)
      window.clearTimeout(animationTimer)
      removeListeners.forEach((remove) => remove())
      const reveal = () => {
        document.documentElement.dataset.intro = "seen"
        try { window.sessionStorage.setItem(INTRO_SESSION_KEY, "1") } catch { /* Storage is optional. */ }
        setVisible(false)
      }
      const duration = Math.min(EXIT_MS, remaining())
      if (preference.matches || duration === 0) reveal()
      else {
        if (duration !== EXIT_MS) setExitDuration(duration)
        setExiting(true)
        exitTimer = window.setTimeout(reveal, duration)
      }
    }
    deadlineTimer = window.setTimeout(() => finish(true), Math.max(0, remaining() - EXIT_MS))
    const onAnimationDone = () => {
      animationReady = true
      finish()
    }
    const onAnimationEnd = (event: Event) => {
      if (event.target === logo && (event as AnimationEvent).animationName === "brand-intro-timeline") onAnimationDone()
    }
    logo?.addEventListener("animationend", onAnimationEnd)
    removeListeners.push(() => logo?.removeEventListener("animationend", onAnimationEnd))
    // Also resolve if hydration starts after the CSS animation has ended.
    if (!preference.matches) animationTimer = window.setTimeout(onAnimationDone, Math.max(0, HOLD_END_MS - (Date.now() - startedAt)))
    const parsed = new Promise<void>((resolve) => {
      if (document.readyState !== "loading") resolve()
      else {
        const onParsed = () => resolve()
        document.addEventListener("DOMContentLoaded", onParsed, { once: true })
        removeListeners.push(() => document.removeEventListener("DOMContentLoaded", onParsed))
      }
    })
    // Analytics and offscreen lazy images must not hold the page behind the cover.
    const svgImages = [...new Set(Array.from(document.querySelectorAll("svg image"))
      .map((image) => image.getAttribute("href"))
      .filter((href): href is string => Boolean(href)))]
      .map((href) => {
        const image = new Image()
        image.src = href
        return image
      })
    const images = [...Array.from(document.images), ...svgImages]
      .filter((image) => image.loading !== "lazy" && !image.complete)
      .map((image) => new Promise<void>((resolve) => {
        const done = () => resolve()
        image.addEventListener("load", done, { once: true })
        image.addEventListener("error", done, { once: true })
        removeListeners.push(() => {
          image.removeEventListener("load", done)
          image.removeEventListener("error", done)
        })
      }))
    const fonts = !document.fonts || document.fonts.status === "loaded"
      ? Promise.resolve()
      : document.fonts.ready.catch(() => {})
    const assetsReady = Promise.all([fonts, ...images])
    const onPreferenceChange = () => { if (preference.matches) onAnimationDone() }
    preference.addEventListener("change", onPreferenceChange)
    removeListeners.push(() => preference.removeEventListener("change", onPreferenceChange))

    Promise.all([parsed, assetsReady]).then(() => {
      if (cancelled) return
      pageReady = true
      finish()
    })

    return () => {
      cancelled = true
      window.clearTimeout(deadlineTimer)
      window.clearTimeout(animationTimer)
      window.clearTimeout(exitTimer)
      removeListeners.forEach((remove) => remove())
    }
  }, [])

  if (!visible) return null
  return (
    <div
      className={`brand-intro${exiting ? " is-exiting" : ""}`}
      role="status"
      aria-label="ORKEST"
      style={{
        "--intro-timeline-duration": `${TIMELINE_MS}ms`,
        "--intro-hold-duration": `${HOLD_END_MS}ms`,
        "--intro-exit-duration": `${exitDuration}ms`,
      } as CSSProperties}
    >
      <HeroBackground animated />
      <svg viewBox={logoViewBox} className="brand-intro-logo relative" data-node-id="60:3" focusable="false" aria-hidden="true">
        {logoLetters.map((group) => {
          const stageIndex = STAGES.findIndex((stage) => stage.letters.includes(group.letter))
          const stage = STAGES[stageIndex]
          return (
            <g
              key={group.letter}
              data-letter={group.letter}
              data-node-id={stage.nodeIds[stage.letters.indexOf(group.letter)]}
              className="brand-intro-assemble"
              style={{
                "--center-offset": `${stage.centerOffset}px`,
              } as CSSProperties}
            >
              <g className={`brand-intro-visibility${stageIndex < 2 ? ` brand-intro-preview-${group.letter.toLowerCase()}` : ""}`}>
                <g className={group.letter === "R" ? "brand-intro-r" : undefined}>
                  {group.paths.map((d, i) => <path key={i} d={d} fill="#023493" />)}
                </g>
              </g>
            </g>
          )
        })}
      </svg>
    </div>
  )
}
